import {
  RevenueIntelligenceChatDealFact,
  RevenueIntelligenceChatFacts,
  RevenueIntelligenceChatInsight,
  RevenueIntelligenceChatMetric,
  RevenueIntelligenceChatDataset,
  RevenueIntelligenceChatChartSpec,
  RevenueIntelligenceChatRecord,
  RevenueIntelligenceChatRecordSet,
} from "./RevenueIntelligenceChatContext";
import {
  asksForRevenueIntelligenceEmailEvidence,
  type RevenueIntelligenceSemanticPlan,
} from "../lib/revenue-intelligence-semantic-planner";

export type ChatIntentId =
  | "access_denied"
  | "capabilities"
  | "management_attention"
  | "recommended_actions"
  | "explain_visible_insight"
  | "explain_visible_insights"
  | "open_pipeline_summary"
  | "open_pipeline_by_stage"
  | "largest_open_deal"
  | "smallest_open_deal"
  | "top_open_deals"
  | "deals_expected_to_close_this_month"
  | "deals_expected_to_close_this_quarter"
  | "deals_expected_to_close_next_60_days"
  | "won_deals_this_month"
  | "won_deals_this_quarter"
  | "lost_deals_this_month"
  | "lost_deals_this_quarter"
  | "average_open_deal_size"
  | "record_set"
  | "source"
  | "clarification"
  | "unsupported";

type ChatPeriod = "month" | "quarter" | "next_60_days";
type ChatOutcome = "open" | "won" | "lost" | "expected";
type ChatTopic = string;

export type RevenueIntelligenceChatTurnContext = {
  intentId: ChatIntentId;
  contractId?: string;
  insightId?: string;
  metricId?: string;
  recordSetId?: string;
  topic?: ChatTopic;
  period?: ChatPeriod;
  outcome?: ChatOutcome;
  recordIds?: string[];
  selectedFields?: string[];
};

export type ChatIntentResponse = {
  intentId: ChatIntentId;
  answer: string;
  context: RevenueIntelligenceChatTurnContext;
  followUpQuestions: string[];
};

const unsupportedAnswer = [
  "I cannot answer that reliably from the approved data available in this view yet.",
  "",
  "Try asking what needs management attention, why a signal matters, what action to take, which deals are involved, or for the source. I can also answer about open pipeline, stage, expected close timing and won or lost deals.",
].join("\n");

const swedishFollowUpCopy: Record<string, string> = {
  "Why are these ranked this way?": "Varför är de rankade så?",
  "What is the source?": "Vad är källan?",
  "Show the records": "Visa underlaget",
  "What needs management attention?": "Vad behöver ledningens uppmärksamhet?",
  "Why?": "Varför?",
  "What should we do?": "Vad bör vi göra?",
  "Chart this": "Rita en graf",
  "What needs attention?": "Vad behöver uppmärksamhet?",
  "Show Forecast against Target": "Visa Forecast mot mål",
  "Chart Pipeline Risk by severity": "Rita Pipeline Risk per risknivå",
  "Save this analysis": "Spara analysen",
  "Show another view": "Visa en annan vy",
  "Why is the deal at risk?": "Varför är dealen riskfylld?",
  "What does the email say?": "Vad stod i mejlet?",
  "What does it mean?": "Vad betyder det?",
  "Why does it matter?": "Varför är det viktigt?",
};

export function localizeRevenueIntelligenceFollowUps(
  languageSample: string,
  questions: string[],
) {
  const usesSwedish = isRevenueIntelligenceSwedish(languageSample);
  return usesSwedish
    ? questions.map(question => swedishFollowUpCopy[question] ?? question)
    : questions;
}

export function isRevenueIntelligenceSwedish(languageSample: string) {
  const normalized = languageSample.toLocaleLowerCase();
  return /[åäö]/.test(normalized) || [
    "vilka", "visa", "varför", "vad", "dealen", "affär", "riskfylld",
    "ägare", "totalt", "plats", "rita", "graf", "jämför", "deras",
  ].some(term => normalized.includes(term));
}

function getApprovedAnswerField(answer: string, label: string) {
  const prefix = `${label}:`;
  const line = answer.split("\n").find(item => item.startsWith(prefix));
  return line?.slice(prefix.length).trim() || null;
}

function localizeApprovedRiskEvidenceToSwedish(evidence: string) {
  const localized = evidence
    .replace(/^Seller engagement:\s*/u, "")
    .replace(/No CRM update for (\d+) days/gu, "ingen CRM-uppdatering på $1 dagar")
    .replace(
      /Engagement balance:\s*The seller sent ([\d.,]+) emails for every buyer email/gu,
      (_, ratio: string) => `kommunikationen är ensidig: säljaren har skickat ${ratio.replace(".", ",")} mejl för varje mejl från köparen`,
    )
    .replace(/Buyer engagement:\s*No buyer interaction for (\d+) days/gu, "ingen köparinteraktion på $1 dagar")
    .replace(
      /Pipeline concentration:\s*(?:Part of the top 20% of deals with >50% of pipeline value|This deal is among the 20% of deals that together represent more than half of pipeline value)\.?/giu,
      "affären ingår i den femtedel av affärerna som tillsammans står för mer än hälften av pipelinevärdet",
    )
    .replace(/;\s*/gu, ". ");
  return localized
    .split(". ")
    .map(sentence => sentence.replace(/^./u, character => character.toLocaleUpperCase()))
    .join(". ");
}

function localizeApprovedBusinessCopyToSwedish(answer: string) {
  return answer
    .replace(/\bDeal:/gmu, "Affär:")
    .replace(/^Business implication:/gmu, "Betydelse:")
    .replace(/^Exact email excerpt:/gmu, "Köparen skrev:")
    .replace(/^Source:/gmu, "Källa:")
    .replace(/Signal type: Risk signal/giu, "Typ: Risksignal")
    .replace(/Signal type: Buying signal/giu, "Typ: Köpsignal")
    .replace(/Signal type: risk_signal/giu, "Typ: Risksignal")
    .replace(/Signal type: buying_signal/giu, "Typ: Köpsignal")
    .replace(/Buyer is evaluating a competing option/giu, "Köparen utvärderar ett konkurrerande alternativ")
    .replace(/Buyer indicates a possible delay/giu, "Köparen signalerar en möjlig försening")
    .replace(/Buyer indicates a budget constraint/giu, "Köparen signalerar ett budgethinder")
    .replace(/Buyer engagement suggests low priority/giu, "Köparens engagemang tyder på låg prioritet")
    .replace(/Buyer has agreed a concrete next step/giu, "Köparen har gått med på ett konkret nästa steg")
    .replace(
      /Pipeline concentration:\s*(?:Part of the top 20% of deals with >50% of pipeline value|This deal is among the 20% of deals that together represent more than half of pipeline value)\.?/giu,
      "Affären ingår i den femtedel av affärerna som tillsammans står för mer än hälften av pipelinevärdet.",
    )
    .replace(/Recommended action:/giu, "Rekommenderad åtgärd:")
    .replace(/Why the deal is at risk:/giu, "Varför affären är riskfylld:")
    .split("\n")
    .map((line) => {
      const numbered = line.match(/^(\d+)\.\s+(.+)$/u);
      if (!numbered) return line;
      const [, position, content] = numbered;
      const parts = content.split(" · ");
      const type = parts.find(part => part.startsWith("Typ: "))?.slice("Typ: ".length);
      const deal = parts.find(part => part.startsWith("Affär: "))?.slice("Affär: ".length);
      const signal = parts.find(part => part.startsWith("Signal: "))?.slice("Signal: ".length);
      if (!type || !deal || !signal || !["Risksignal", "Köpsignal"].includes(type)) return line;
      const naturalType = type === "Risksignal" ? "risk" : "köpsignal";
      const naturalSignal = signal.replace(/^./u, character => character.toLocaleLowerCase());
      return `${position}. ${deal}: ${naturalType} – ${naturalSignal}.`;
    })
    .join("\n");
}

function localizeApprovedActionToSwedish(action: string, owner: string | null) {
  const ownerName = owner ?? "Ansvarig säljare";
  const normalizedAction = action.trim();

  if (!normalizedAction || normalizedAction === "Not specified") return null;

  if (normalizedAction.includes("Buyer engagement has stalled")) {
    return `${ownerName} bör bekräfta att köparens intresse fortfarande är aktivt och säkra ett tydligt nästa steg.`;
  }
  if (
    normalizedAction.includes("Confirm whether buyer activity matches the CRM status")
    && normalizedAction.includes("is still driving the deal")
  ) {
    return `${ownerName} bör kontrollera att köparaktiviteten stämmer med CRM-statusen och att affären fortfarande drivs aktivt.`;
  }
  if (normalizedAction.includes("Overdue close date is a large risk factor")) {
    return `${ownerName} bör bekräfta köparens åtagande innan affären ligger kvar i forecast.`;
  }
  if (normalizedAction.includes("The deal has stalled")) {
    return `${ownerName} bör bekräfta köparens åtagande och kontrollera att affären fortfarande är aktiv.`;
  }
  if (normalizedAction.includes("Buyer response times are increasing")) {
    return `${ownerName} bör återkoppla till nyckelpersonerna och återfå momentum i affären.`;
  }
  if (normalizedAction.includes("Buyer engagement is low")) {
    return `${ownerName} bör återbekräfta affärsvärdet med köparen innan mer säljtid investeras.`;
  }
  if (normalizedAction.includes("Repeated close date slippage is a strong risk indicator")) {
    return `${ownerName} bör bekräfta behov, tidskritikalitet och beslutsansvar. Om köparens åtagande inte kan bekräftas bör affären omprövas.`;
  }
  if (normalizedAction.includes("Deal progression is slow")) {
    return `${ownerName} bör identifiera vad som blockerar affären och skapa tydlighet kring nästa beslut.`;
  }
  if (normalizedAction.includes("Stage regression is a major risk factor")) {
    return `${ownerName} bör kvalificera om affären innan den drivs vidare.`;
  }
  if (normalizedAction.includes("No next activity is scheduled")) {
    return `${ownerName} bör boka ett tydligt nästa steg och bekräfta vägen till avslut.`;
  }
  if (normalizedAction.includes("Buyer communication shows hesitation")) {
    return `${ownerName} bör lyfta köparens tveksamhet och hantera de invändningar som återstår.`;
  }
  if (normalizedAction.includes("Reconfirm buyer commitment")) {
    return `${ownerName} bör bekräfta köparens åtagande på nytt.`;
  }
  if (normalizedAction.includes("Re-engage the decision maker")) {
    return `${ownerName} bör återuppta dialogen med beslutsfattaren.`;
  }
  return null;
}

export function localizeRevenueIntelligenceApprovedFallback(
  approvedAnswer: string,
  question: string,
) {
  if (!isRevenueIntelligenceSwedish(question)) return approvedAnswer;
  const comparison = approvedAnswer.match(
    /^(.+?) has the (higher|lower) ([^:]+): ([^.]+)\. (.+?) has ([^.]+)\.$/u,
  );
  if (comparison) {
    const [, leader, direction, rawField, leaderValue, other, otherValue] = comparison;
    const field = rawField === "Signal strength"
      ? "signalstyrka"
      : rawField.toLocaleLowerCase();
    return `${leader} har ${direction === "higher" ? "högre" : "lägre"} ${field}: ${leaderValue}. ${other} har ${otherValue}.`;
  }
  const missingNamedRecord = approvedAnswer.match(
    /^No matching record was found for:\s*(.+)\.$/u,
  );
  if (missingNamedRecord?.[1]) {
    return `Jag hittar ingen signal för ${missingNamedRecord[1]} i det tillgängliga underlaget.`;
  }
  const lostEffortShare = approvedAnswer.match(
    /^Effort spent on lost deals\s*\n([\d.,]+)%/u,
  );
  if (
    lostEffortShare?.[1]
    && includesAny(normalizeQuestion(question), [
      "mest tid på vunna eller förlorade affärer",
      "tid på vunna eller förlorade affärer",
    ])
  ) {
    const share = Number(lostEffortShare[1].replace(",", "."));
    const comparison = share > 50
      ? "Mest tid lades på förlorade affärer."
      : share < 50
        ? "Mest tid lades på vunna affärer."
        : "Lika mycket tid lades på vunna och förlorade affärer.";
    return `${comparison} ${lostEffortShare[1]}% av säljtiden gick till förlorade affärer.`;
  }
  if (approvedAnswer.startsWith(unsupportedAnswer.split("\n")[0] ?? unsupportedAnswer)) {
    return [
      "Jag kan inte svara tillförlitligt från det godkända underlaget ännu.",
      "",
      "Fråga om vad som behöver uppmärksamhet, varför en signal är viktig, vad ni bör göra, vilka affärer som berörs eller källan.",
    ].join("\n");
  }
  const deal = getApprovedAnswerField(approvedAnswer, "Deal");
  const owner = getApprovedAnswerField(approvedAnswer, "Owner");
  const riskScore = getApprovedAnswerField(approvedAnswer, "Pipeline Risk score");
  const riskEvidence = getApprovedAnswerField(approvedAnswer, "Why the deal is at risk");
  const recommendedAction = getApprovedAnswerField(approvedAnswer, "Recommended action");
  const source = getApprovedAnswerField(approvedAnswer, "Source");
  const normalizedQuestion = normalizeQuestion(question);

  if (
    recommendedAction
    && includesAny(normalizedQuestion, ["vad bör", "vad ska", "göra", "åtgärd"])
  ) {
    if (recommendedAction === "Not specified") {
      return deal
        ? `För ${deal} finns ingen godkänd rekommenderad första åtgärd i underlaget.`
        : "Det finns ingen godkänd rekommenderad första åtgärd i underlaget.";
    }
    return localizeApprovedActionToSwedish(recommendedAction, owner)
      ?? localizeApprovedBusinessCopyToSwedish(approvedAnswer);
  }
  if (
    deal
    && riskEvidence
    && includesAny(normalizedQuestion, ["varför", "riskfylld", "riskabel"])
  ) {
    return `${deal} är framför allt riskfylld på grund av låg säljaraktivitet. ${localizeApprovedRiskEvidenceToSwedish(riskEvidence)}.`;
  }
  if (
    riskEvidence
    && includesAny(normalizedQuestion, [
      "underlag", "källa", "bevis", "hur vet", "vad stödjer",
    ])
  ) {
    return [
      deal
        ? `Underlaget för ${deal} visar följande:`
        : "Underlaget visar följande:",
      localizeApprovedRiskEvidenceToSwedish(riskEvidence),
      source ? `Källa: ${source}` : null,
    ].filter((line): line is string => Boolean(line)).join("\n\n");
  }
  if (deal && owner && riskScore && includesAny(normalizedQuestion, ["vem", "äger", "ägare"])) {
    const score = riskScore.replace(/\s*(?:pts|points|poäng)$/iu, "");
    return `${owner} äger dealen med högst risk: ${deal}, med ${score} riskpoäng.`;
  }
  return localizeApprovedBusinessCopyToSwedish(approvedAnswer);
}

function normalizeQuestion(question: string) {
  return question.toLowerCase().trim().replace(/[?!.,]+$/g, "");
}

function includesAny(question: string, terms: string[]) {
  return terms.some(term => question.includes(term));
}

function isHistoricalMovementQuestion(question: string) {
  return includesAny(question, [
    "over time",
    "since the start",
    "since year",
    "year to date",
    "ytd",
    "trend",
    "developed",
    "changed",
    "movement",
    "över tid",
    "sedan årsskift",
    "sen årsskift",
    "utveckl",
    "förändr",
    "trend",
  ]);
}

function isRiskQuestion(
  question: string,
  previous: RevenueIntelligenceChatTurnContext | null,
) {
  return previous?.topic === "risk" || includesAny(question, [
    "risk",
    "risk exposure",
    "riskexponering",
    "high-risk",
    "högrisk",
  ]);
}

function response(
  intentId: ChatIntentId,
  answer: string,
  context: Omit<RevenueIntelligenceChatTurnContext, "intentId"> = {},
  followUpQuestions: string[] = [],
): ChatIntentResponse {
  return {
    intentId,
    answer,
    context: { ...context, intentId },
    followUpQuestions,
  };
}

export function isUnsupportedRevenueIntelligenceChatResponse(value: ChatIntentResponse) {
  return value.intentId === "unsupported";
}

function findInsightById(
  insightId: string | undefined,
  insights: RevenueIntelligenceChatInsight[],
) {
  return insightId
    ? insights.find(insight => insight.insightId === insightId)
    : undefined;
}

function findInsightForQuestion(
  question: string,
  insights: RevenueIntelligenceChatInsight[],
) {
  const contractTopicInsight = insights.find(insight => (
    insight.topics?.some(topic => question.includes(normalizeQuestion(topic)))
  ));
  if (contractTopicInsight) return contractTopicInsight;

  if (includesAny(question, [
    "coverage", "funnel", "pipeline shape", "early-stage", "mid-stage", "late-stage",
    "täckning", "pipelineform",
  ])) {
    return insights.find(insight => (
      insight.insightId === "management_review.coverage_pattern"
      || insight.insightId.includes("coverage")
    ));
  }

  if (includesAny(question, ["waterfall", "delta", "movement", "förändring"])) {
    return insights.find(insight => insight.insightId.includes("movement"));
  }

  if (includesAny(question, ["forecast", "prognos"])) {
    return insights.find(insight => (
      insight.insightId === "management_review.forecast_movement"
      || insight.insightId.includes("forecast.executive")
      || insight.insightId.includes("forecast")
    ));
  }

  if (includesAny(question, [
    "urgency", "near-term", "attention", "risk", "urgent", "uppmärksamhet",
  ])) {
    return insights.find(insight => (
      insight.insightId === "management_review.near_term_exposure"
      || insight.insightId.includes("management_attention")
      || insight.insightId.includes("risk")
    ));
  }

  return undefined;
}

function buildInsightExplanation(insight: RevenueIntelligenceChatInsight) {
  const lines = [insight.title, "", insight.body];

  if (insight.recommendedAction) {
    lines.push("", `Management action: ${insight.recommendedAction}`);
  }
  if (insight.caveat) {
    lines.push("", `Limitation: ${insight.caveat}`);
  }
  lines.push("", `Evidence: ${insight.evidence}`);

  return lines.join("\n");
}

function buildManagementAttentionAnswer(insights: RevenueIntelligenceChatInsight[]) {
  const managementInsights = getManagementAttentionInsights(insights);
  if (managementInsights.length === 0) {
    return "No approved management signals are available in this view.";
  }

  return [
    "Management attention",
    "",
    ...managementInsights.flatMap((insight, index) => [
      `${index + 1}. ${insight.title}`,
      insight.body,
      insight.recommendedAction ? `Action: ${insight.recommendedAction}` : null,
      "",
    ]),
  ].filter(line => line !== null).join("\n").trim();
}

function getManagementAttentionInsights(insights: RevenueIntelligenceChatInsight[]) {
  return insights
    .map((insight, index) => ({
      index,
      insight,
      priority: insight.recommendedAction
        ? 0
        : insight.insightId.includes("management_attention")
          ? 1
          : 2,
    }))
    .toSorted((a, b) => a.priority - b.priority || a.index - b.index)
    .slice(0, 3)
    .map(item => item.insight);
}

function buildRecommendedActionsAnswer(insights: RevenueIntelligenceChatInsight[]) {
  const actionable = getActionableInsights(insights);
  if (actionable.length === 0) {
    return "No approved management actions are available in this view.";
  }

  return [
    "Recommended management actions",
    "",
    ...actionable.slice(0, 3).map((insight, index) => (
      `${index + 1}. ${insight.recommendedAction}\nWhy: ${insight.title}`
    )),
  ].join("\n");
}

function getActionableInsights(insights: RevenueIntelligenceChatInsight[]) {
  return insights.filter(insight => insight.recommendedAction);
}

function buildEvidenceSummary(insights: RevenueIntelligenceChatInsight[]) {
  return [
    "Sources for the current management signals",
    "",
    ...insights.flatMap(insight => [
      `${insight.label}: ${insight.evidence}`,
      `Source: ${insight.sourceLabel ?? insight.label}`,
      "",
    ]),
  ].join("\n").trim();
}

function findMetricForQuestion(
  question: string,
  metrics: RevenueIntelligenceChatMetric[],
) {
  return metrics.find(metric => (
    [metric.label, ...metric.aliases]
      .map(normalizeQuestion)
      .some(alias => alias.length > 2 && question.includes(alias))
  ));
}

function buildMetricAnswer(metric: RevenueIntelligenceChatMetric) {
  return [
    metric.label,
    "",
    metric.valueLabel,
    "",
    metric.definition,
    "",
    `Scope: ${metric.includedRecords}`,
    `Date basis: ${metric.dateField}`,
    `Value basis: ${metric.valueBasis}`,
    `Currency: ${metric.currencyHandling}`,
    `Probability/risk: ${metric.probabilityOrRiskBasis}`,
    ...(metric.caveats?.length ? ["", `Limitations: ${metric.caveats.join(" ")}`] : []),
    "",
    `Source: ${metric.sourceLabel ?? metric.label}`,
  ].join("\n");
}

function findRecordSetForQuestion(
  question: string,
  recordSets: RevenueIntelligenceChatRecordSet[],
) {
  return recordSets.find(recordSet => (
    [recordSet.label, ...recordSet.aliases]
      .map(normalizeQuestion)
      .some(alias => alias.length > 2 && question.includes(alias))
  ));
}

function buildRecordSetAnswer(recordSet: RevenueIntelligenceChatRecordSet) {
  if (recordSet.records.length === 0) {
    return [
      recordSet.emptyMessage,
      "",
      `Selection: ${recordSet.rankingDescription}`,
      `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
    ].join("\n");
  }

  return [
    recordSet.responseTitle,
    "",
    ...recordSet.records.slice(0, 8).map((record, index) => (
      `${index + 1}. ${record.label}${record.details.length ? ` — ${record.details.join(" · ")}` : ""}`
    )),
    ...(recordSet.records.length > 8
      ? [`+ ${recordSet.records.length - 8} more`]
      : []),
    "",
    `Selection: ${recordSet.rankingDescription}`,
    ...(recordSet.caveats?.length
      ? [`Limitations: ${recordSet.caveats.join(" ")}`]
      : []),
    `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
  ].join("\n");
}

function compareRecordValues(
  left: string | number | null,
  right: string | number | null,
  direction: "asc" | "desc",
) {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  const multiplier = direction === "asc" ? 1 : -1;
  if (typeof left === "number" && typeof right === "number") {
    return (left - right) * multiplier;
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  }) * multiplier;
}

function normalizeRecordFilterValue(value: unknown) {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, " ")
    .trim();
}

function resolveRecordFilterValues(
  recordSet: RevenueIntelligenceChatRecordSet,
  records: RevenueIntelligenceChatRecord[],
  fieldName: string,
  requestedValues: string[],
) {
  const field = recordSet.fields.find(item => item.field === fieldName);
  const candidates = records.map(record => ({
    normalized: normalizeRecordFilterValue(record.values[fieldName]),
  }));
  return requestedValues.map((requestedValue) => {
    const normalized = normalizeRecordFilterValue(requestedValue);
    const exact = candidates.find(candidate => candidate.normalized === normalized);
    if (exact) return exact.normalized;
    if (field?.type !== "category" || normalized.length < 3) return normalized;
    const partial = candidates.filter(candidate => (
      candidate.normalized.includes(normalized)
      || normalized.includes(candidate.normalized)
    ));
    return partial.length === 1 ? partial[0].normalized : normalized;
  });
}

export function resolveRevenueIntelligenceRecordQuery(
  recordSet: RevenueIntelligenceChatRecordSet,
  query: NonNullable<RevenueIntelligenceSemanticPlan["recordQuery"]>,
  activeRecordIds: string[] = [],
  parentRecordIds: string[] = [],
) {
  if (!recordSet.records.length) return [];
  const recordsById = new Map(recordSet.records.map(record => [record.id, record]));
  const scopedRecordIds = query.scope === "active_result"
    ? activeRecordIds
    : query.scope === "parent_result"
      ? parentRecordIds
      : null;
  const sourceRecords = scopedRecordIds
    ? scopedRecordIds.flatMap(id => {
        const record = recordsById.get(id);
        return record ? [record] : [];
      })
    : [...recordSet.records];
  const orderBy = query.orderBy
    ?? (query.scope === "record_set" ? recordSet.defaultSort : null);
  const resolvedFilters = query.filters.map(filter => ({
    ...filter,
    values: resolveRecordFilterValues(
      recordSet,
      sourceRecords,
      filter.field,
      filter.values,
    ),
  }));
  const filtered = sourceRecords
    .filter(record => resolvedFilters.every(filter => {
      const recordValue = normalizeRecordFilterValue(record.values[filter.field]);
      const filterValues = filter.values;
      return filter.operator === "equals"
        ? recordValue === filterValues[0]
        : filterValues.includes(recordValue);
    }));
  const sorted = orderBy
    ? filtered.toSorted((left, right) => compareRecordValues(
        left.values[orderBy.field] ?? null,
        right.values[orderBy.field] ?? null,
        orderBy.direction,
      ))
    : filtered;
  return sorted.slice(query.offset, query.offset + query.limit);
}

function buildGroupedRecordSetAnswer(
  recordSet: RevenueIntelligenceChatRecordSet,
  records: RevenueIntelligenceChatRecord[],
  groupBy: string,
  minimumGroupSize: number,
) {
  const field = recordSet.fields.find(item => item.field === groupBy);
  if (!field) return recordSet.emptyMessage;
  const groups = new Map<string, RevenueIntelligenceChatRecord[]>();
  for (const record of records) {
    const value = record.displayValues?.[groupBy]
      ?? String(record.values[groupBy] ?? "Not specified");
    groups.set(value, [...(groups.get(value) ?? []), record]);
  }
  const matchingGroups = [...groups.entries()]
    .filter(([, rows]) => rows.length >= minimumGroupSize)
    .toSorted((left, right) => right[1].length - left[1].length);
  if (!matchingGroups.length) {
    return minimumGroupSize > 1
      ? `No records in the selected set share the same ${field.label}.`
      : recordSet.emptyMessage;
  }
  return matchingGroups.map(([value, rows]) => (
    `${field.label}: ${value}\n${rows.map(row => `- ${row.label}`).join("\n")}`
  )).join("\n\n");
}

function buildQueriedRecordSetResponse(
  recordSet: RevenueIntelligenceChatRecordSet,
  query: NonNullable<RevenueIntelligenceSemanticPlan["recordQuery"]>,
  operation: RevenueIntelligenceSemanticPlan["operation"],
  activeRecordIds: string[],
  parentRecordIds: string[],
) {
  const records = resolveRevenueIntelligenceRecordQuery(
    recordSet,
    query,
    activeRecordIds,
    parentRecordIds,
  );
  if (!records.length) {
    const identifierFields = new Set(recordSet.fields.flatMap(field => (
      field.semanticRoles?.includes("identifier") ? [field.field] : []
    )));
    const namedFilter = query.filters.find(filter => identifierFields.has(filter.field));
    return {
      answer: namedFilter
        ? `No matching record was found for: ${namedFilter.values.join(", ")}.`
        : recordSet.emptyMessage,
      records,
    };
  }
  if (operation === "group" && query.groupBy) {
    return {
      answer: [
        buildGroupedRecordSetAnswer(
          recordSet,
          records,
          query.groupBy,
          query.minimumGroupSize,
        ),
        "",
        `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
      ].join("\n"),
      records,
    };
  }
  if (
    operation === "compare"
    && query.orderBy
    && query.scope === "parent_result"
    && activeRecordIds.length === 1
  ) {
    const focused = recordSet.records.find(record => record.id === activeRecordIds[0]);
    const comparisonRecords = resolveRevenueIntelligenceRecordQuery(
      recordSet,
      {
        ...query,
        limit: Math.max(1, parentRecordIds.length),
        offset: 0,
        scope: "parent_result",
      },
      activeRecordIds,
      parentRecordIds,
    );
    const top = comparisonRecords[0];
    const topValue = top?.values[query.orderBy.field] ?? null;
    const leaders = comparisonRecords.filter(record => (
      record.values[query.orderBy?.field ?? ""] === topValue
    ));
    const focusedValue = focused?.values[query.orderBy.field] ?? null;
    const field = recordSet.fields.find(item => item.field === query.orderBy?.field);
    if (focused && top && field) {
      const focusedLabel = getRecordIdentifierLabel(recordSet, focused);
      const topLabel = getRecordIdentifierLabel(recordSet, top);
      const focusedDisplay = focused.displayValues?.[field.field]
        ?? String(focusedValue ?? "Not specified");
      const leaderDisplay = top.displayValues?.[field.field]
        ?? String(topValue ?? "Not specified");
      const isLeader = leaders.some(record => record.id === focused.id);
      const tiedLeaders = leaders.filter(record => record.id !== focused.id);
      return {
        answer: isLeader && tiedLeaders.length
          ? `${focusedLabel} and ${tiedLeaders.map(record => getRecordIdentifierLabel(recordSet, record)).join(" and ")} have the same highest ${field.label}: ${focusedDisplay}.`
          : isLeader
            ? `Yes. ${focusedLabel} has the highest ${field.label}: ${focusedDisplay}.`
          : `No. ${focusedLabel} has ${field.label} ${focusedDisplay}. ${leaders.map(record => getRecordIdentifierLabel(recordSet, record)).join(" and ")} ${leaders.length === 1 ? "has" : "have"} the highest ${field.label}: ${leaderDisplay}.`,
        records: [focused, ...leaders.filter(record => record.id !== focused.id)],
      };
    }
  }
  if (operation === "compare" && query.orderBy && records.length >= 2) {
    const field = recordSet.fields.find(item => item.field === query.orderBy?.field);
    const leader = records[0];
    const leaderValue = leader?.values[query.orderBy.field] ?? null;
    const leaders = records.filter(record => (
      record.values[query.orderBy?.field ?? ""] === leaderValue
    ));
    if (leader && field) {
      const leaderLabel = getRecordIdentifierLabel(recordSet, leader);
      const leaderDisplay = leader.displayValues?.[field.field]
        ?? String(leaderValue ?? "Not specified");
      if (leaders.length === records.length) {
        return {
          answer: `${records.map(record => getRecordIdentifierLabel(recordSet, record)).join(" and ")} have the same ${field.label}: ${leaderDisplay}.`,
          records,
        };
      }
      const comparison = records.find(record => record.id !== leader.id);
      const comparisonDisplay = comparison
        ? comparison.displayValues?.[field.field]
          ?? String(comparison.values[field.field] ?? "Not specified")
        : null;
      const directionLabel = query.orderBy.direction === "desc" ? "higher" : "lower";
      return {
        answer: comparison && records.length === 2
          ? `${leaderLabel} has the ${directionLabel} ${field.label}: ${leaderDisplay}. ${getRecordIdentifierLabel(recordSet, comparison)} has ${comparisonDisplay}.`
          : `${leaders.map(record => getRecordIdentifierLabel(recordSet, record)).join(" and ")} ${leaders.length === 1 ? "has" : "have"} the ${query.orderBy.direction === "desc" ? "highest" : "lowest"} ${field.label}: ${leaderDisplay}.`,
        records,
      };
    }
  }
  const requestedFields = query.selectFields.flatMap(fieldName => {
    const field = recordSet.fields.find(item => item.field === fieldName);
    return field ? [field] : [];
  });
  const fields = recordSet.recordSetId === "revenue_intelligence.persistent_signals.active"
    && ["list", "rank"].includes(operation)
    && requestedFields.length > 4
    ? ["deal", "signal_type", "signal", "management_implication"].flatMap(fieldName => {
        const field = recordSet.fields.find(item => item.field === fieldName);
        return field ? [field] : [];
      })
    : requestedFields;
  const rows = records.map((record, index) => {
    const values = fields.flatMap(field => {
      const value = record.displayValues?.[field.field]
        ?? String(record.values[field.field] ?? "Not specified");
      if (
        recordSet.recordSetId === "revenue_intelligence.persistent_signals.active"
        && /^No approved /u.test(value)
      ) return [];
      return [`${field.label}: ${value}`];
    });
    return records.length === 1
      ? values.join("\n")
      : `${index + 1}. ${values.join(" · ")}`;
  });
  const selectedCurrencyFields = fields.filter(field => field.type === "currency");
  const totalLine = operation === "value"
    && records.length > 1
    && selectedCurrencyFields.length === 1
    ? buildRecordCurrencyTotal(records, selectedCurrencyFields[0])
    : null;

  return {
    answer: [
      ...rows,
      ...(totalLine ? ["", totalLine] : []),
      "",
      `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
    ].join("\n"),
    records,
  };
}

function getRecordIdentifierLabel(
  recordSet: RevenueIntelligenceChatRecordSet,
  record: RevenueIntelligenceChatRecord,
) {
  const identifierField = recordSet.fields.find(field => (
    field.semanticRoles?.includes("identifier")
  ));
  if (!identifierField) return record.label;
  return record.displayValues?.[identifierField.field]
    ?? String(record.values[identifierField.field] ?? record.label);
}

export function answerRevenueIntelligenceActiveSignalPriorityFollowUp(
  question: string,
  recordSet: RevenueIntelligenceChatRecordSet | undefined,
  activeRecordIds: string[],
): ChatIntentResponse | null {
  if (
    recordSet?.recordSetId !== "revenue_intelligence.persistent_signals.active"
    || !activeRecordIds.length
  ) return null;
  const normalized = normalizeQuestion(question);
  const asksForPriority = includesAny(normalized, [
    "most important",
    "highest priority",
    "top priority",
    "viktigast",
    "högst prioriter",
  ]);
  const asksForSignal = includesAny(normalized, [
    "signal",
    "risksignal",
    "köpsignal",
  ]);
  if (!asksForPriority || !asksForSignal) return null;

  const recordsById = new Map(recordSet.records.map(record => [record.id, record]));
  const activeRecords = activeRecordIds.flatMap(id => {
    const record = recordsById.get(id);
    return record ? [record] : [];
  });
  const asksForRisk = includesAny(normalized, ["risk signal", "risksignal"]);
  const asksForBuying = includesAny(normalized, ["buying signal", "köpsignal"]);
  const selected = activeRecords.find(record => (
    asksForRisk
      ? record.values.signal_type === "risk_signal"
      : asksForBuying
        ? record.values.signal_type === "buying_signal"
        : true
  ));
  if (!selected) return null;

  const deal = selected.displayValues?.deal ?? String(selected.values.deal ?? "Not specified");
  const signal = selected.displayValues?.signal ?? String(selected.values.signal ?? "Not specified");
  const implication = selected.displayValues?.management_implication
    ?? String(selected.values.management_implication ?? "Not specified");
  return response(
    "record_set",
    [
      `Deal: ${deal}`,
      `Signal: ${signal}`,
      ...(implication === "Not specified" ? [] : [`Business implication: ${implication}`]),
      "",
      `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
    ].join("\n"),
    {
      recordSetId: recordSet.recordSetId,
      topic: recordSet.label,
      recordIds: [selected.id],
      selectedFields: ["deal", "signal", "management_implication"],
    },
    ["What does the email say?", "What should we do?"],
  );
}

export function answerRevenueIntelligenceActiveSignalDetailFollowUp(
  question: string,
  recordSet: RevenueIntelligenceChatRecordSet | undefined,
  activeRecordIds: string[],
): ChatIntentResponse | null {
  if (
    recordSet?.recordSetId !== "revenue_intelligence.persistent_signals.active"
    || !activeRecordIds.length
  ) return null;
  const normalized = normalizeQuestion(question);
  const asksForEvidence = asksForRevenueIntelligenceEmailEvidence(normalized)
    || includesAny(normalized, ["exact quote", "email excerpt", "exakt citat"]);
  const asksForAction = includesAny(normalized, [
    "what should we do",
    "what should i do",
    "recommended action",
    "next step",
    "vad bör vi göra",
    "vad ska vi göra",
    "vad bör jag göra",
    "rekommenderad åtgärd",
    "nästa steg",
  ]);
  const asksForMeaning = includesAny(normalized, [
    "what does it mean",
    "why does it matter",
    "business implication",
    "vad betyder det",
    "varför är den viktig",
    "varför spelar den roll",
    "affärsmässig betydelse",
  ]);
  if (!asksForEvidence && !asksForAction && !asksForMeaning) return null;

  const recordsById = new Map(recordSet.records.map(record => [record.id, record]));
  const selected = activeRecordIds.flatMap(id => {
    const record = recordsById.get(id);
    return record ? [record] : [];
  })[0];
  if (!selected) return null;
  const deal = selected.displayValues?.deal ?? String(selected.values.deal ?? "Not specified");
  const commonContext = {
    recordSetId: recordSet.recordSetId,
    topic: recordSet.label,
    recordIds: [selected.id],
  };

  if (asksForEvidence) {
    const evidence = selected.displayValues?.evidence
      ?? String(selected.values.evidence ?? "Not specified");
    return response(
      "record_set",
      evidence === "Not specified"
        ? `Deal: ${deal}\nNo exact email excerpt is available for this Signal.`
        : [
            `Deal: ${deal}`,
            `Exact email excerpt: ${evidence}`,
            "",
            `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
          ].join("\n"),
      { ...commonContext, selectedFields: ["deal", "evidence"] },
      ["What does it mean?", "What should we do?"],
    );
  }

  if (asksForAction) {
    const action = selected.displayValues?.recommended_action
      ?? String(selected.values.recommended_action ?? "Not specified");
    return response(
      "record_set",
      action === "Not specified" || /^No approved /u.test(action)
        ? `Deal: ${deal}\nNo approved recommended action is available for this Signal.`
        : `Deal: ${deal}\nRecommended action: ${action}`,
      { ...commonContext, selectedFields: ["deal", "recommended_action"] },
      ["What does the email say?", "Why does it matter?"],
    );
  }

  const implication = selected.displayValues?.management_implication
    ?? String(selected.values.management_implication ?? "Not specified");
  return response(
    "record_set",
    implication === "Not specified" || /^No approved /u.test(implication)
      ? `Deal: ${deal}\nNo approved business implication is available for this Signal.`
      : `Deal: ${deal}\nBusiness implication: ${implication}`,
    { ...commonContext, selectedFields: ["deal", "management_implication"] },
    ["What does the email say?", "What should we do?"],
  );
}

function buildRecordCurrencyTotal(
  records: RevenueIntelligenceChatRecord[],
  field: RevenueIntelligenceChatRecordSet["fields"][number],
) {
  const values = records.map(record => record.values[field.field]);
  if (!values.every(value => typeof value === "number" && Number.isFinite(value))) {
    return null;
  }
  const total = values.reduce<number>((sum, value) => sum + Number(value), 0);
  const sample = records
    .map(record => record.displayValues?.[field.field])
    .find((value): value is string => Boolean(value));
  const currency = sample?.match(/\b(?:[kM])?([A-Z]{3})\b/u)?.[1];
  if (!currency) return `Total ${field.label}: ${total}`;
  const absolute = Math.abs(total);
  const scale = absolute >= 1_000_000
    ? { divisor: 1_000_000, prefix: "M" }
    : absolute >= 1_000
      ? { divisor: 1_000, prefix: "k" }
      : { divisor: 1, prefix: "" };
  const formatted = new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 1,
    minimumFractionDigits: scale.divisor === 1 ? 0 : 1,
  }).format(total / scale.divisor);
  return `Total ${field.label}: ${formatted} ${scale.prefix}${currency}`;
}

export function buildRevenueIntelligenceRecordChartDataset({
  activeRecordIds,
  parentRecordIds = [],
  chart,
  query,
  recordSet,
}: {
  activeRecordIds: string[];
  parentRecordIds?: string[];
  chart: RevenueIntelligenceChatChartSpec;
  query: NonNullable<RevenueIntelligenceSemanticPlan["recordQuery"]>;
  recordSet: RevenueIntelligenceChatRecordSet;
}): RevenueIntelligenceChatDataset | null {
  const records = resolveRevenueIntelligenceRecordQuery(
    recordSet,
    query,
    activeRecordIds,
    parentRecordIds,
  );
  if (!records.length) return null;
  const selectedFieldNames = new Set([
    ...query.selectFields,
    chart.xField,
    ...chart.yFields,
    ...(chart.groupBy ? [chart.groupBy] : []),
  ]);
  const fields = recordSet.fields.filter(field => selectedFieldNames.has(field.field));
  const dimensions = fields.filter(field => ["category", "date"].includes(field.type));
  const measures = fields.filter(field => ["currency", "number", "percentage"].includes(field.type));
  const numericChartFields = [
    ...(chart.type === "scatter" ? [chart.xField] : []),
    ...chart.yFields,
  ];
  if (records.some(record => numericChartFields.some((field) => {
    const value = record.values[field];
    return typeof value !== "number" || !Number.isFinite(value);
  }))) return null;
  return {
    datasetId: `record_result:${recordSet.recordSetId}`,
    label: recordSet.label,
    description: `Selected ${recordSet.label.toLocaleLowerCase()} from the active analysis.`,
    dimensions,
    measures,
    rows: records.map(record => ({
      ...record.values,
      __record_id: record.id,
    })),
    allowedChartTypes: [chart.type],
    defaultChart: chart,
    sourceHelper: recordSet.sourceHelper,
    sourceFields: recordSet.sourceFields,
    caveats: recordSet.caveats,
  };
}

export function answerRevenueIntelligenceSemanticPlan(
  plan: RevenueIntelligenceSemanticPlan,
  insights: RevenueIntelligenceChatInsight[],
  metrics: RevenueIntelligenceChatMetric[],
  recordSets: RevenueIntelligenceChatRecordSet[],
  activeRecordIds: string[] = [],
  parentRecordIds: string[] = [],
): ChatIntentResponse | null {
  if (plan.clarification.needed && plan.clarification.question) {
    return response(
      "clarification",
      plan.clarification.question,
      {},
      [],
    );
  }

  if (plan.intent === "capabilities" || plan.operation === "capabilities") {
    return response(
      "capabilities",
      [
        "You can ask me to explain approved Revenue Intelligence metrics and signals, review ranked deal evidence, or create a chart from available datasets.",
        "",
        "You can refine a chart with follow-ups, then export or save the analysis.",
      ].join("\n"),
      {},
      ["Show Forecast against Target", "Chart Pipeline Risk by severity", "What needs attention?"],
    );
  }

  const metric = plan.metricIds.length
    ? metrics.find(item => item.metricId === plan.metricIds[0])
    : undefined;
  const insight = plan.insightId
    ? insights.find(item => item.insightId === plan.insightId)
    : undefined;
  const recordSet = plan.recordSetId
    ? recordSets.find(item => item.recordSetId === plan.recordSetId)
    : undefined;

  if (recordSet) {
    if (plan.operation === "source" || plan.operation === "definition") {
      return response(
        "source",
        [
          recordSet.rankingDescription,
          "",
          `Source: ${recordSet.sourceLabel ?? recordSet.label}`,
          ...(recordSet.caveats?.length
            ? ["", `Limitations: ${recordSet.caveats.join(" ")}`]
            : []),
        ].join("\n"),
        { recordSetId: recordSet.recordSetId, topic: recordSet.label },
        ["Show the records", "What needs management attention?"],
      );
    }
    const queried = plan.recordQuery
      ? buildQueriedRecordSetResponse(
          recordSet,
          plan.recordQuery,
          plan.operation,
          activeRecordIds,
          parentRecordIds,
        )
      : null;
    return response(
      "record_set",
      queried
        ? queried.answer
        : buildRecordSetAnswer(recordSet),
      {
        contractId: plan.contractId ?? undefined,
        recordSetId: recordSet.recordSetId,
        topic: recordSet.label,
        recordIds: queried?.records.map(record => record.id)
          ?? recordSet.records.map(record => record.id),
        selectedFields: plan.recordQuery?.selectFields
          ?? recordSet.fields.map(field => field.field),
      },
      plan.operation === "evidence"
        ? ["What does it mean?", "What should we do?"]
        : plan.operation === "explain"
          ? ["What should we do?", "What is the source?"]
          : plan.operation === "recommend"
            ? ["What is the source?"]
            : ["Why are these ranked this way?", "What is the source?"],
    );
  }

  if (insight) {
    if (plan.operation === "recommend" && insight.recommendedAction) {
      return response(
        "recommended_actions",
        [
          "Management action",
          "",
          insight.recommendedAction,
          "",
          `Why: ${insight.title}`,
        ].join("\n"),
        { insightId: insight.insightId, topic: insight.topics?.[0] },
        ["Why?", "What is the source?"],
      );
    }
    return response(
      "explain_visible_insight",
      buildInsightExplanation(insight),
      { insightId: insight.insightId, topic: insight.topics?.[0] },
      ["What should we do?", "What is the source?"],
    );
  }

  if (metric) {
    return response(
      "source",
      buildMetricAnswer(metric),
      { metricId: metric.metricId, topic: metric.aliases[0] ?? metric.label },
      ["Chart this", "What needs attention?", "What is the source?"],
    );
  }

  return null;
}

function formatDealLine(deal: RevenueIntelligenceChatDealFact, index?: number) {
  const prefix = index === undefined ? "" : `${index + 1}. `;
  const details = [
    deal.valueLabel,
    deal.stageLabel,
    deal.expectedCloseDateLabel ? `expected ${deal.expectedCloseDateLabel}` : null,
    deal.closedDateLabel ? `closed ${deal.closedDateLabel}` : null,
    deal.riskLabel,
  ].filter(Boolean).join(" · ");

  return `${prefix}${deal.name}${details ? ` — ${details}` : ""}`;
}

function withSource(lines: string[], facts: RevenueIntelligenceChatFacts) {
  return [...lines, "", `Source: ${facts.source}`].join("\n");
}

function buildSingleDealAnswer(
  title: string,
  deal: RevenueIntelligenceChatDealFact | undefined,
  facts: RevenueIntelligenceChatFacts,
) {
  return withSource(
    deal ? [title, "", formatDealLine(deal)] : ["No matching deal found."],
    facts,
  );
}

function buildDealListAnswer(
  title: string,
  deals: RevenueIntelligenceChatDealFact[],
  emptyText: string,
  facts: RevenueIntelligenceChatFacts,
) {
  if (deals.length === 0) return withSource([emptyText], facts);

  return withSource([
    title,
    "",
    ...deals.slice(0, 8).map(formatDealLine),
    ...(deals.length > 8 ? [`+ ${deals.length - 8} more`] : []),
  ], facts);
}

function getDetectedPeriod(question: string): ChatPeriod | undefined {
  if (includesAny(question, ["next 60", "60 days", "60 dagar", "nästa 60"])) {
    return "next_60_days";
  }
  if (includesAny(question, ["quarter", "kvartal"])) return "quarter";
  if (includesAny(question, ["this month", "denna månad", "den här månaden", "månad"])) {
    return "month";
  }
  return undefined;
}

function getDetectedOutcome(question: string): ChatOutcome | undefined {
  if (includesAny(question, ["won", "vunna", "vunnit"])) return "won";
  if (includesAny(question, ["lost", "förlorade", "förlorat"])) return "lost";
  if (includesAny(question, ["expected", "closing", "close", "stäng", "förväntas"])) return "expected";
  if (includesAny(question, ["open", "öppen", "öppna"])) return "open";
  return undefined;
}

function answerOutcomePeriodQuestion(
  outcome: ChatOutcome | undefined,
  period: ChatPeriod | undefined,
  facts: RevenueIntelligenceChatFacts,
): ChatIntentResponse | null {
  if (outcome === "won" && period === "month") {
    return response(
      "won_deals_this_month",
      buildDealListAnswer(
        `Won this month — ${facts.wonRevenueThisMonthLabel}`,
        facts.wonDealsThisMonth,
        "No won deals found this month.",
        facts,
      ),
      { topic: "performance", outcome, period },
      ["What about this quarter?", "What was lost this month?", "What is the source?"],
    );
  }
  if (outcome === "won" && period === "quarter") {
    return response(
      "won_deals_this_quarter",
      buildDealListAnswer(
        `Won this quarter — ${facts.wonRevenueThisQuarterLabel}`,
        facts.wonDealsThisQuarter,
        "No won deals found this quarter.",
        facts,
      ),
      { topic: "performance", outcome, period },
      ["What was lost this quarter?", "What is the source?"],
    );
  }
  if (outcome === "lost" && period === "month") {
    return response(
      "lost_deals_this_month",
      buildDealListAnswer(
        `Lost this month — ${facts.lostDealValueThisMonthLabel}`,
        facts.lostDealsThisMonth,
        "No lost deals found this month.",
        facts,
      ),
      { topic: "performance", outcome, period },
      ["What about this quarter?", "What was won this month?", "What is the source?"],
    );
  }
  if (outcome === "lost" && period === "quarter") {
    return response(
      "lost_deals_this_quarter",
      buildDealListAnswer(
        `Lost this quarter — ${facts.lostDealValueThisQuarterLabel}`,
        facts.lostDealsThisQuarter,
        "No lost deals found this quarter.",
        facts,
      ),
      { topic: "performance", outcome, period },
      ["What was won this quarter?", "What is the source?"],
    );
  }
  if (outcome === "expected" && period === "month") {
    return response(
      "deals_expected_to_close_this_month",
      buildDealListAnswer(
        "Expected to close this month",
        facts.expectedToCloseThisMonth,
        "No open deals are expected to close this month.",
        facts,
      ),
      { topic: "forecast", outcome, period },
      ["What about this quarter?", "Which is the largest?", "What is the source?"],
    );
  }
  if (outcome === "expected" && period === "quarter") {
    return response(
      "deals_expected_to_close_this_quarter",
      buildDealListAnswer(
        "Expected to close this quarter",
        facts.expectedToCloseThisQuarter,
        "No open deals are expected to close this quarter.",
        facts,
      ),
      { topic: "forecast", outcome, period },
      ["What about the next 60 days?", "What needs attention?", "What is the source?"],
    );
  }
  if (outcome === "expected" && period === "next_60_days") {
    return response(
      "deals_expected_to_close_next_60_days",
      buildDealListAnswer(
        "Expected to close in the next 60 days",
        facts.expectedToCloseNext60Days,
        "No open deals are expected to close in the next 60 days.",
        facts,
      ),
      { topic: "forecast", outcome, period },
      ["What about this quarter?", "What needs attention?", "What is the source?"],
    );
  }
  return null;
}

function answerContextualFollowUp(
  question: string,
  insights: RevenueIntelligenceChatInsight[],
  facts: RevenueIntelligenceChatFacts | null,
  previous: RevenueIntelligenceChatTurnContext | null,
  metrics: RevenueIntelligenceChatMetric[],
  recordSets: RevenueIntelligenceChatRecordSet[],
): ChatIntentResponse | null {
  if (!previous) return null;

  const previousInsight = findInsightById(previous.insightId, insights);
  const previousMetric = previous.metricId
    ? metrics.find(metric => metric.metricId === previous.metricId)
    : undefined;
  const previousRecordSet = previous.recordSetId
    ? recordSets.find(recordSet => recordSet.recordSetId === previous.recordSetId)
    : undefined;
  const asksWhy = includesAny(question, ["why", "explain", "varför", "förklara", "how so"]);
  const asksAction = includesAny(question, [
    "what should", "what do we do", "action", "next step", "vad ska", "åtgärd", "göra åt",
  ]);
  const asksSource = includesAny(question, [
    "source", "evidence", "based on", "how do you know", "källa", "underlag",
  ]);
  const asksCalculation = includesAny(question, [
    "calculate", "calculated", "definition", "how is", "how do you calculate",
    "beräkna", "beräknas", "definition",
  ]);

  if (
    previousRecordSet
    && previous.recordIds?.length
    && includesAny(question, [
      "pipeline share",
      "share of pipeline",
      "share of open pipeline",
      "andel av pipeline",
      "andel av öppen pipeline",
      "hur stor del av pipeline",
    ])
  ) {
    const pipelineShareField = previousRecordSet.fields.find(
      field => field.field === "pipeline_share",
    );
    const activeRecord = previousRecordSet.records.find(
      record => previous.recordIds?.includes(record.id),
    );
    if (pipelineShareField && activeRecord) {
      const pipelineShare = activeRecord.displayValues?.[pipelineShareField.field]
        ?? String(activeRecord.values[pipelineShareField.field] ?? "Not specified");
      return response(
        "record_set",
        isRevenueIntelligenceSwedish(question)
          ? `${activeRecord.label} står för ${pipelineShare} av öppen pipeline.`
          : `${activeRecord.label} represents ${pipelineShare} of open pipeline.`,
        {
          ...previous,
          recordSetId: previousRecordSet.recordSetId,
          recordIds: [activeRecord.id],
          selectedFields: [pipelineShareField.field],
        },
        ["What is the source?", "Why is the deal at risk?"],
      );
    }
  }

  if ((asksSource || asksCalculation) && previousMetric) {
    return response(
      "source",
      buildMetricAnswer(previousMetric),
      { ...previous, metricId: previousMetric.metricId },
      ["What needs attention?", "What can I ask?"],
    );
  }

  if ((asksSource || asksWhy || asksCalculation) && previousRecordSet) {
    return response(
      "source",
      [
        previousRecordSet.rankingDescription,
        "",
        `Source: ${previousRecordSet.sourceLabel ?? previousRecordSet.label}`,
        ...(previousRecordSet.caveats?.length
          ? ["", `Limitations: ${previousRecordSet.caveats.join(" ")}`]
          : []),
      ].join("\n"),
      { ...previous, recordSetId: previousRecordSet.recordSetId },
      ["What needs management attention?", "What else can you answer?"],
    );
  }

  if (asksWhy && previousInsight) {
    return response(
      "explain_visible_insight",
      buildInsightExplanation(previousInsight),
      { ...previous, insightId: previousInsight.insightId },
      ["What should we do?", "What is the source?", "What else needs attention?"],
    );
  }

  if (asksAction && previousInsight?.recommendedAction) {
    return response(
      "recommended_actions",
      [
        "Management action",
        "",
        previousInsight.recommendedAction,
        "",
        `Why: ${previousInsight.title}`,
      ].join("\n"),
      { ...previous, insightId: previousInsight.insightId },
      ["Why?", "What is the source?", "What else needs attention?"],
    );
  }

  if (asksAction && insights.length > 0) {
    const actionableInsights = getActionableInsights(insights);
    return response(
      "recommended_actions",
      buildRecommendedActionsAnswer(insights),
      {
        insightId: actionableInsights[0]?.insightId,
        topic: previous.topic,
      },
      ["Why is the first action important?", "What is the source?"],
    );
  }

  if (asksSource) {
    if (previousInsight) {
      return response(
        "source",
        [
          `Evidence: ${previousInsight.evidence}`,
          "",
          `Source: ${previousInsight.sourceLabel ?? previousInsight.label}`,
          ...(previousInsight.caveat ? ["", `Limitation: ${previousInsight.caveat}`] : []),
        ].join("\n"),
        { ...previous, insightId: previousInsight.insightId },
        ["What should we do?", "What else needs attention?"],
      );
    }
    if (facts) {
      return response(
        "source",
        [
          `Source: ${facts.source}`,
          ...(facts.scope ? [
            "",
            `View: ${facts.scope.pageLabel}${facts.scope.periodLabel ? ` · ${facts.scope.periodLabel}` : ""}`,
          ] : []),
        ].join("\n"),
        previous,
        ["What needs attention?", "What can you answer?"],
      );
    }
  }

  if (facts) {
    const detectedPeriod = getDetectedPeriod(question);
    const detectedOutcome = getDetectedOutcome(question);
    const inheritedPeriod = detectedPeriod ?? (
      detectedOutcome && !detectedPeriod ? previous.period : undefined
    );
    const inheritedOutcome = detectedOutcome ?? (
      detectedPeriod && !detectedOutcome ? previous.outcome : undefined
    );
    const periodResponse = answerOutcomePeriodQuestion(
      inheritedOutcome,
      inheritedPeriod,
      facts,
    );
    if (periodResponse) return periodResponse;

    if (includesAny(question, ["largest", "biggest", "största", "largest one"])) {
      return response(
        "largest_open_deal",
        buildSingleDealAnswer("Largest open deal", facts.largestOpenDeal, facts),
        { topic: "pipeline", outcome: "open" },
        ["What is the source?", "Show the top open deals"],
      );
    }
  }

  return null;
}

function answerDatabaseFactQuestion(
  question: string,
  facts: RevenueIntelligenceChatFacts | null,
): ChatIntentResponse | null {
  if (!facts) return null;

  const outcomePeriodAnswer = answerOutcomePeriodQuestion(
    getDetectedOutcome(question),
    getDetectedPeriod(question),
    facts,
  );
  if (outcomePeriodAnswer) return outcomePeriodAnswer;

  if (includesAny(question, [
    "open pipeline by stage", "pipeline by stage", "pipeline per stage",
    "pipeline per steg", "pipeline per fas", "öppen pipeline per",
  ])) {
    const lines = facts.openPipelineByStage.length === 0
      ? ["No open pipeline by stage found."]
      : [
          "Open pipeline by stage",
          "",
          ...facts.openPipelineByStage.slice(0, 8).map(stage => (
            `${stage.stageLabel}: ${stage.totalValueLabel} · ${stage.count} deals`
          )),
        ];
    return response(
      "open_pipeline_by_stage",
      withSource(lines, facts),
      { topic: "pipeline", outcome: "open" },
      ["Which deals are largest?", "What needs management attention?", "What is the source?"],
    );
  }

  if (includesAny(question, [
    "largest open", "biggest open", "största öppna", "största open",
  ])) {
    return response(
      "largest_open_deal",
      buildSingleDealAnswer("Largest open deal", facts.largestOpenDeal, facts),
      { topic: "pipeline", outcome: "open" },
      ["Show the top open deals", "What is the source?"],
    );
  }

  if (includesAny(question, ["smallest open", "minsta öppna", "smallest deal"])) {
    return response(
      "smallest_open_deal",
      buildSingleDealAnswer("Smallest open deal", facts.smallestOpenDeal, facts),
      { topic: "pipeline", outcome: "open" },
      ["Show the largest open deal", "What is the source?"],
    );
  }

  if (includesAny(question, [
    "top open", "top deals", "largest deals", "biggest deals",
    "största deals", "största öppna deals",
  ])) {
    return response(
      "top_open_deals",
      buildDealListAnswer("Top open deals", facts.topOpenDeals, "No open deals found.", facts),
      { topic: "pipeline", outcome: "open" },
      ["Which is the largest?", "What needs attention?", "What is the source?"],
    );
  }

  if (includesAny(question, [
    "open pipeline", "pipeline total", "pipeline value", "hur stor är pipeline",
    "öppen pipeline", "open pipe",
  ])) {
    return response(
      "open_pipeline_summary",
      withSource([
        "Open pipeline",
        "",
        `${facts.openPipelineValueLabel} across ${facts.openDealCount} open deals.`,
        `Average open deal size: ${facts.averageOpenDealSizeLabel}.`,
      ], facts),
      { topic: "pipeline", outcome: "open" },
      ["Show it by stage", "Which deals are largest?", "What needs attention?"],
    );
  }

  if (includesAny(question, [
    "average deal", "avg deal", "average open deal", "snittdeal", "genomsnittlig deal",
  ])) {
    return response(
      "average_open_deal_size",
      withSource(["Average open deal size", "", facts.averageOpenDealSizeLabel], facts),
      { topic: "performance", outcome: "open" },
      ["Show the largest open deals", "What is the source?"],
    );
  }

  return null;
}

export function getRevenueIntelligenceChatOpening(
  insights: RevenueIntelligenceChatInsight[],
  facts: RevenueIntelligenceChatFacts | null,
  metrics: RevenueIntelligenceChatMetric[] = [],
  recordSets: RevenueIntelligenceChatRecordSet[] = [],
) {
  const leadingInsight = insights[0];
  if (leadingInsight) {
    return {
      eyebrow: "Management signal",
      message: leadingInsight.title,
      question: "Why does this need attention?",
      context: {
        intentId: "explain_visible_insight" as const,
        insightId: leadingInsight.insightId,
        topic: "risk" as const,
      },
    };
  }
  if (facts) {
    return {
      eyebrow: facts.scope?.pageLabel ?? "Current view",
      message: `${facts.openPipelineValueLabel} across ${facts.openDealCount} open deals.`,
      question: "What needs management attention?",
      context: {
        intentId: "open_pipeline_summary" as const,
        topic: "pipeline" as const,
        outcome: "open" as const,
      },
    };
  }
  const leadingMetric = metrics[0];
  if (leadingMetric) {
    return {
      eyebrow: "Current metric",
      message: `${leadingMetric.label}: ${leadingMetric.valueLabel}`,
      question: `How is ${leadingMetric.label} calculated?`,
      context: {
        intentId: "source" as const,
        metricId: leadingMetric.metricId,
        topic: leadingMetric.aliases[0] ?? leadingMetric.label,
      },
    };
  }
  const leadingRecordSet = recordSets.find(recordSet => recordSet.records.length > 0);
  if (leadingRecordSet) {
    return {
      eyebrow: leadingRecordSet.label,
      message: `${leadingRecordSet.records.length} records are available for review.`,
      question: leadingRecordSet.aliases[0] ?? leadingRecordSet.label,
      context: {
        intentId: "record_set" as const,
        recordSetId: leadingRecordSet.recordSetId,
        topic: leadingRecordSet.label,
      },
    };
  }
  return null;
}

export function answerRevenueIntelligenceQuestion(
  question: string,
  insights: RevenueIntelligenceChatInsight[],
  facts: RevenueIntelligenceChatFacts | null,
  previous: RevenueIntelligenceChatTurnContext | null = null,
  metrics: RevenueIntelligenceChatMetric[] = [],
  accessNotice: string | null = null,
  recordSets: RevenueIntelligenceChatRecordSet[] = [],
): ChatIntentResponse {
  const normalizedQuestion = normalizeQuestion(question);

  if (
    accessNotice
    && !insights.length
    && !facts
    && !metrics.length
    && !recordSets.length
  ) {
    return response(
      "access_denied",
      [
        "I cannot answer that from data outside your permitted scope.",
        "",
        accessNotice,
        "",
        "Use a view filtered to your own deals, or ask an administrator or authorised manager for broader access.",
      ].join("\n"),
      {},
      ["What can I ask?"],
    );
  }

  if (
    isHistoricalMovementQuestion(normalizedQuestion)
    && isRiskQuestion(normalizedQuestion, previous)
  ) {
    const historicalRiskInsight = insights.find(insight => (
      includesAny(insight.insightId, ["movement", "history", "trend"])
      && (
        insight.topics?.some(topic => normalizeQuestion(topic).includes("risk"))
        || insight.insightId.includes("risk")
      )
    ));
    if (historicalRiskInsight) {
      return response(
        "explain_visible_insight",
        buildInsightExplanation(historicalRiskInsight),
        {
          insightId: historicalRiskInsight.insightId,
          topic: "risk",
        },
        ["What should we do?", "What is the source?"],
      );
    }
    return response(
      "unsupported",
      [
        "I can’t show how risk exposure has developed over time from this view yet.",
        "",
        "The approved data shows current exposure, but not a comparable historical risk-exposure series.",
      ].join("\n"),
      { topic: "risk" },
      ["Show current risk exposure", "What needs management attention?"],
    );
  }

  if (includesAny(normalizedQuestion, [
    "what can you answer", "what can i ask", "help", "vad kan du svara",
    "vad kan jag fråga",
  ])) {
    return response(
      "capabilities",
      [
        "You can ask me to:",
        "",
        "• Identify what needs management attention",
        "• Explain why a visible signal matters",
        "• Recommend the approved management action",
        "• Show the evidence and calculation source",
        "• Explore open pipeline by stage or deal",
        ...(recordSets.length ? ["• Review approved ranked deal lists"] : []),
        "• Review expected close timing",
        "• Review won and lost deals by month or quarter",
        "",
        "You can use short follow-ups such as “Why?”, “What should we do?”, “Which deals?” or “What about this quarter?”.",
      ].join("\n"),
      {},
      ["What needs management attention?", "What should we do?", "Which deals close this quarter?"],
    );
  }

  const contextualAnswer = answerContextualFollowUp(
    normalizedQuestion,
    insights,
    facts,
    previous,
    metrics,
    recordSets,
  );
  if (contextualAnswer) return contextualAnswer;

  if (includesAny(normalizedQuestion, [
    "what needs attention", "management attention", "most important",
    "what should we focus", "priority", "priorities", "vad behöver uppmärksamhet",
    "vad är viktigast", "fokusera på",
  ])) {
    const managementInsights = getManagementAttentionInsights(insights);
    const hasManagementSignals = managementInsights.length > 0;
    return response(
      "management_attention",
      buildManagementAttentionAnswer(insights),
      {
        insightId: managementInsights[0]?.insightId,
        topic: "risk",
      },
      hasManagementSignals
        ? ["Why does the first signal matter?", "What should we do?", "What is the source?"]
        : ["What can you answer?"],
    );
  }

  if (includesAny(normalizedQuestion, [
    "what should we do", "recommended action", "next action",
    "vad ska vi göra", "rekommenderad åtgärd",
  ])) {
    const actionableInsights = getActionableInsights(insights);
    const hasApprovedActions = actionableInsights.length > 0;
    return response(
      "recommended_actions",
      buildRecommendedActionsAnswer(insights),
      {
        insightId: actionableInsights[0]?.insightId,
        topic: "risk",
      },
      hasApprovedActions
        ? ["Why is the first action important?", "What is the source?"]
        : ["What can you answer?"],
    );
  }

  const recordSet = findRecordSetForQuestion(normalizedQuestion, recordSets);
  if (recordSet) {
    return response(
      "record_set",
      buildRecordSetAnswer(recordSet),
      {
        recordSetId: recordSet.recordSetId,
        topic: recordSet.label,
      },
      ["Why are these ranked this way?", "What is the source?", "What needs management attention?"],
    );
  }

  const databaseFactAnswer = answerDatabaseFactQuestion(normalizedQuestion, facts);
  if (databaseFactAnswer) return databaseFactAnswer;

  const metric = findMetricForQuestion(normalizedQuestion, metrics);
  const asksForMetricDefinition = includesAny(normalizedQuestion, [
    "calculate", "calculated", "definition", "how much", "what is the value",
    "beräkna", "beräknas", "värde",
  ]);
  if (metric && asksForMetricDefinition) {
    return response(
      "source",
      buildMetricAnswer(metric),
      {
        metricId: metric.metricId,
        topic: metric.aliases[0] ?? metric.label,
      },
      [`How is ${metric.label} calculated?`, "What needs attention?", "What is the source?"],
    );
  }

  const insight = findInsightForQuestion(normalizedQuestion, insights);
  if (insight) {
    const topic: ChatTopic = insight.insightId.includes("forecast")
      ? "forecast"
      : insight.insightId.includes("coverage")
        ? "pipeline"
        : "risk";
    return response(
      "explain_visible_insight",
      buildInsightExplanation(insight),
      { insightId: insight.insightId, topic },
      ["Why?", "What should we do?", "What is the source?"],
    );
  }

  if (metric) {
    return response(
      "source",
      buildMetricAnswer(metric),
      {
        metricId: metric.metricId,
        topic: metric.aliases[0] ?? metric.label,
      },
      [`How is ${metric.label} calculated?`, "What needs attention?", "What is the source?"],
    );
  }

  if (includesAny(normalizedQuestion, [
    "evidence", "source", "based on", "how do you know", "källa", "underlag",
  ]) && insights.length > 0) {
    return response(
      "explain_visible_insights",
      buildEvidenceSummary(insights),
      {},
      ["What needs attention?", "What should we do?"],
    );
  }

  return response(
    "unsupported",
    unsupportedAnswer,
    {},
    ["What needs management attention?", "What should we do?", "What can you answer?"],
  );
}
